import * as THREE from 'three';
import { SkyScene } from './scene/SkyScene';
import { PaperPlane } from './scene/PaperPlane';
import { Effects } from './scene/Effects';
import { FlightSim } from './physics/flight';
import { ThrowInput } from './input/throwInput';
import { Menu } from './ui/Menu';
import { HUD } from './ui/HUD';
import { LetterModal } from './ui/LetterModal';
import { PostcardShare } from './ui/PostcardShare';
import { MuteButton } from './ui/MuteButton';
import { QrModal } from './ui/QrModal';
import { Tutorial } from './ui/Tutorial';
import { showFatal, showToast } from './ui/Toast';
import { SoundBoard } from './audio/SoundBoard';
import { classifyEmotion, streamChat, type ChatMessage } from './ai/chat';
import {
  appendTurn,
  buildSystemPromptWithMemory,
  compressLocally,
  loadMemory,
  recordSharedEvent,
  saveMemory,
  shouldCompress
} from './ai/memory';
import {
  isAchievementShown,
  isFullCollectionUnlocked,
  markAchievementShown,
  recordVisit,
  visitedSet
} from './ai/visits';
import type { Island } from './scene/Island';

const appEl = document.querySelector<HTMLDivElement>('#app');
const uiRootEl = document.querySelector<HTMLDivElement>('#ui-root');
if (!appEl || !uiRootEl) throw new Error('app/ui-root not found');

let sky: SkyScene;
try {
  sky = new SkyScene(appEl, {
    onPolycount: n => console.info('[whisper-sky] polycount =', n)
  });
} catch (err) {
  console.error('[whisper-sky] WebGL init failed', err);
  showFatal('WebGL 不可用 · 请用 Chrome / Safari 最新版打开');
  throw err;
}

const qrModal = new QrModal(uiRootEl);
const menu = new Menu(uiRootEl, {
  onScanQr: () => { void qrModal.open(); }
});
const hud = new HUD(uiRootEl);
const letter = new LetterModal(uiRootEl);
const postcard = new PostcardShare(uiRootEl);
const tutorial = new Tutorial(uiRootEl);
const sound = new SoundBoard();
const mute = new MuteButton(uiRootEl, {
  onToggle: muted => sound.setMuted(muted)
});

hud.setVisited(visitedSet());

const plane = new PaperPlane();
plane.setHidden(true);
plane.attachTrail(sky.scene);
sky.scene.add(plane.mesh);

const effects = new Effects(sky.scene);

const flight = new FlightSim();
const launchOrigin = new THREE.Vector3(0, 4, 12);
plane.mesh.position.copy(launchOrigin);

let isThrowing = false;
let lockedIsland: Island | null = null;
let modalOpen = false;
let lastLetterText = '';
let lastReplyText = '';

const aimDot = createAimDot(uiRootEl);

const throwInput = new ThrowInput(appEl, {
  isEnabled: () => !modalOpen && !flight.alive,
  onAim: v => {
    if (!v) {
      aimDot.style.opacity = '0';
      hud.update({ planesLeft: Infinity, hint: '滑动屏幕蓄力 · 松手投出', aimPower: null, lockedPersona: null });
      return;
    }
    aimDot.style.opacity = '1';
    aimDot.style.transform = `translate(-50%, -50%) scale(${0.6 + v.power * 0.9})`;
    hud.update({
      planesLeft: Infinity,
      hint: `蓄力 ${Math.round(v.power * 100)}%`,
      aimPower: v.power,
      lockedPersona: null
    });
  },
  onRelease: v => {
    aimDot.style.opacity = '0';
    launchPlane(v);
  }
});

menu.waitForStart().then(async () => {
  hud.show();
  mute.show();
  hud.update({ planesLeft: Infinity, hint: '滑动屏幕蓄力 · 松手投出', aimPower: null, lockedPersona: null });
  sound.startBgmOnFirstInteraction();
  if (tutorial.shouldShow()) {
    await tutorial.show();
  }
});

sky.onUpdate((_t, dt) => {
  effects.update(dt);
  if (!flight.alive) {
    plane.mesh.position.copy(launchOrigin);
    plane.mesh.rotation.set(0, 0, 0);
    return;
  }
  flight.step(dt);
  applyHoming(dt);
  plane.mesh.position.copy(flight.position);
  flight.orient(plane.mesh);
  plane.pushTrail(flight.position);

  const hit = sky.pickIsland(flight.position, 3.6);
  const fellThroughFloor = flight.position.y < -3;
  const tooFar = flight.position.lengthSq() > 70 * 70;

  if (hit) {
    onArrived(hit);
  } else if (fellThroughFloor || tooFar) {
    if (fellThroughFloor) effects.spawnRipple(flight.position.clone(), 0xc8e5ff);
    flight.reset();
    plane.setHidden(true);
    plane.resetTrail();
    isThrowing = false;
    homingDisabled = false;
    hud.update({ planesLeft: Infinity, hint: '没飞到岛上 · 再来一次', aimPower: null, lockedPersona: null });
  }
});

let homingDisabled = false;

function applyHoming(dt: number): void {
  if (!flight.alive) return;
  if (homingDisabled) return;
  const forward = flight.velocity.clone().normalize();
  let bestDot = Math.cos(THREE.MathUtils.degToRad(45));
  let bestIsland: Island | null = null;
  let bestDist = Infinity;
  for (const island of sky.islands) {
    const toIsland = island.group.position.clone().sub(flight.position);
    const dist = toIsland.length();
    if (dist < 0.001) continue;
    const dir = toIsland.divideScalar(dist);
    const dot = forward.dot(dir);
    if (dot > bestDot && dist < bestDist) {
      bestDot = dot;
      bestIsland = island;
      bestDist = dist;
    }
  }
  if (!bestIsland) return;
  const target = bestIsland.group.position.clone().sub(flight.position).normalize();
  const desired = target.multiplyScalar(flight.velocity.length());
  flight.velocity.lerp(desired, Math.min(1, dt * 1.6));
}

sky.start();

function launchPlane(v: { power: number; yaw: number; pitch: number }): void {
  if (isThrowing || modalOpen) return;
  isThrowing = true;
  plane.resetTrail();
  plane.setHidden(false);

  const speed = 9 + v.power * 14;
  const camForward = new THREE.Vector3();
  sky.camera.getWorldDirection(camForward);
  camForward.y = 0;
  if (camForward.lengthSq() < 0.001) camForward.set(0, 0, -1);
  camForward.normalize();
  const dir = camForward.clone();
  dir.applyAxisAngle(new THREE.Vector3(0, 1, 0), -v.yaw);
  const right = new THREE.Vector3().crossVectors(dir, new THREE.Vector3(0, 1, 0)).normalize();
  dir.applyAxisAngle(right, v.pitch * Math.PI * 0.18);
  const velocity = dir.multiplyScalar(speed);
  flight.spawn({ origin: launchOrigin.clone(), velocity });
  hud.update({ planesLeft: Infinity, hint: '飞行中...', aimPower: null, lockedPersona: null });
  sound.play('launch');
  console.info('[whisper-sky] thrown power=%s yaw=%s pitch=%s', v.power.toFixed(2), v.yaw.toFixed(2), v.pitch.toFixed(2));
}

function onArrived(island: Island): void {
  flight.reset();
  plane.setHidden(true);
  isThrowing = false;
  homingDisabled = false;
  lockedIsland = island;
  console.info(`[arrived] island=${island.personaId}`);
  effects.spawnBurst(island.group.position.clone().add(new THREE.Vector3(0, 1.5, 0)), island.persona.color, island.personaId);
  hud.update({ planesLeft: Infinity, hint: `到达 ${island.persona.shortName} 的岛`, aimPower: null, lockedPersona: island.personaId });
  sound.play('arrived');

  const visit = recordVisit(island.personaId);
  hud.setVisited(visitedSet());
  hud.pulseSlot(island.personaId);
  if (visit.isFirst) {
    console.info(`[milestone] first_visit=${island.personaId}`);
    hud.showFirstMeet(island.personaId);
  }
  if (isFullCollectionUnlocked() && !isAchievementShown()) {
    setTimeout(() => {
      hud.showAchievement('满月信使 · 已认识全部 5 位居民');
      markAchievementShown();
      console.info('[milestone] achievement=full_collection');
    }, visit.isFirst ? 1700 : 200);
  }

  openLetter(island);
}

function openLetter(island: Island): void {
  modalOpen = true;
  letter.open({ persona: island.persona }, {
    onSend: async text => {
      lastLetterText = text;
      const memory = loadMemory(island.personaId);
      const systemPrompt = buildSystemPromptWithMemory(island.persona.system_prompt, memory, island.personaId);
      const messages: ChatMessage[] = [
        ...memory.recent,
        { role: 'user', content: text }
      ];
      let aggregated = '';
      letter.setReply('');
      const finalReply = await streamChat(
        { personaId: island.personaId, systemPrompt, messages },
        chunk => {
          aggregated += chunk;
          letter.appendReplyChunk(chunk);
        }
      );
      lastReplyText = finalReply || aggregated;

      const emotion = classifyEmotion(lastReplyText);
      island.triggerEmotion(emotion.tag, emotion.emoji);
      sound.play('reply');
      letter.flashReplyEdge();

      let nextMemory = appendTurn(memory, text, lastReplyText);
      if (shouldCompress(nextMemory)) nextMemory = compressLocally(nextMemory);
      saveMemory(island.personaId, nextMemory);
      recordSharedEvent(island.personaId, text);
    },
    onClose: () => {
      modalOpen = false;
      lockedIsland = null;
      hud.update({ planesLeft: Infinity, hint: '再投一架，看看别的居民', aimPower: null, lockedPersona: null });
    },
    onPostcard: () => {
      if (!lockedIsland) return;
      postcard.open(
        { persona: lockedIsland.persona, letter: lastLetterText, reply: lastReplyText },
        { onClose: () => {} }
      );
    }
  });
}

function createAimDot(host: HTMLElement): HTMLDivElement {
  const dot = document.createElement('div');
  dot.className = 'layer aim-dot';
  dot.style.opacity = '0';
  host.appendChild(dot);
  return dot;
}

window.addEventListener('beforeunload', () => throwInput.destroy());
window.addEventListener('error', ev => {
  if (ev.error && /webgl/i.test(String(ev.error?.message ?? ''))) {
    showToast('图形渲染异常，请刷新或换浏览器', 'error', 3500);
  }
});

interface WhisperDebugHooks {
  launch: (power: number, yaw: number, pitch: number) => void;
  aimAt: (personaId: string, flightTime?: number) => boolean;
  isThrowing: () => boolean;
  modalOpen: () => boolean;
  islands: () => Array<{ id: string; pos: { x: number; y: number; z: number } }>;
  planePos: () => { x: number; y: number; z: number };
  resetTutorial: () => void;
  resetVisits: () => void;
  visited: () => string[];
}

const debug: WhisperDebugHooks = {
  launch: (power, yaw, pitch) => launchPlane({ power, yaw, pitch }),
  aimAt: (personaId, flightTime = 1.1) => {
    if (isThrowing || modalOpen) return false;
    const island = sky.islands.find(i => i.personaId === personaId);
    if (!island) return false;
    const target = island.group.position;
    const t = flightTime;
    const g = 4.5;
    const drag = 0.12;
    const dragFactor = (1 - Math.exp(-drag * t)) / drag;
    const vx = (target.x - launchOrigin.x) / dragFactor;
    const vz = (target.z - launchOrigin.z) / dragFactor;
    const vy = (target.y - launchOrigin.y) / t + 0.5 * g * t;
    const velocity = new THREE.Vector3(vx, vy, vz);
    plane.resetTrail();
    plane.setHidden(false);
    flight.spawn({ origin: launchOrigin.clone(), velocity });
    homingDisabled = true;
    isThrowing = true;
    hud.update({ planesLeft: Infinity, hint: '飞行中...', aimPower: null, lockedPersona: null });
    sound.play('launch');
    console.info('[whisper-sky] aimAt persona=%s velocity=(%s, %s, %s)', personaId, vx.toFixed(2), vy.toFixed(2), vz.toFixed(2));
    return true;
  },
  isThrowing: () => isThrowing,
  modalOpen: () => modalOpen,
  islands: () => sky.islands.map(i => ({ id: i.personaId, pos: { x: i.group.position.x, y: i.group.position.y, z: i.group.position.z } })),
  planePos: () => ({ x: plane.mesh.position.x, y: plane.mesh.position.y, z: plane.mesh.position.z }),
  resetTutorial: () => { try { localStorage.removeItem('wsky:tutorial:seen'); } catch { /* private mode */ } },
  resetVisits: () => {
    try {
      ['whale','jelly','bear','fox','star','boat'].forEach(id => localStorage.removeItem(`wsky:visits:${id}`));
      localStorage.removeItem('wsky:achievement:full');
    } catch { /* private mode */ }
    hud.setVisited(visitedSet());
  },
  visited: () => Array.from(visitedSet())
};

(window as unknown as { __wsky?: WhisperDebugHooks }).__wsky = debug;
